import type { DifferentiationQuestion } from "../types.js";

/**
 * LOS DOCE DESEMPATES QUE FALTABAN, para cubrir los catorce pares que el canon
 * trata de verdad. Los otros dos —1↔6 y 9↔6— siguen en `draft-situations.ts`,
 * con el alfa que ya está calibrado.
 *
 * CALIBRADOS el 17 de agosto de 2026 por el autor. Regla propia de los
 * desempates: **se puntúa sólo la alternativa elegida**, sin restar a la
 * contraria, porque hacerlo duplicaría artificialmente la distancia entre las
 * dos. Y los que se apoyan en conducta y no en motivo —D_1_8, D_7_8, D_8_9—
 * pesan +1 y no +2: la expresión de la ira o la vaguedad dependen de subtipo y
 * contexto, y no deben valer lo mismo que una diferencia motivacional.
 *
 * POR QUÉ ESTOS CATORCE PARES Y NO OTROS. Salen de leer el Bloque E de los
 * nueve tipos y quedarse con lo que el documento trata, no con lo que parecería
 * razonable. El grafo resultante es **asimétrico**, y conviene saberlo:
 *
 *   1 → 3, 6, 8        9 → 2, 6, 5        (tres cada uno, y no son sus alas)
 *   2 → 1, 3           3 → 2, 4           4 → 3, 5
 *   5 → 4, 6           6 → 5, 7           7 → 6, 8       8 → 7, 9
 *
 * Los tipos 2 a 8 listan EXACTAMENTE sus dos alas — o sea, adyacencia en el
 * círculo, que es una relación geométrica, no una hipótesis sobre qué se
 * confunde con qué. El 1 y el 9, en cambio, listan tipos no adyacentes (1↔3,
 * 1↔8, 9↔5) que son las confusiones clásicas de verdad.
 *
 * **Eso es un límite del material, no del código**: el 1 y el 9 recibieron un
 * análisis de confundibilidad y los otros siete recibieron el vecindario. La
 * consecuencia práctica es que los pares que sólo existen por adyacencia —3↔4,
 * 4↔5, 5↔6, 6↔7, 7↔8— separan peor que 1↔6 o 9↔6, y hay confusiones conocidas
 * que NO están cubiertas porque el canon no las trata (2↔6 y 4↔9, por ejemplo).
 * Van marcadas una a una en `sourceRefs`, y es lo primero que el piloto debería
 * medir.
 *
 * MAPEO, igual que en los dos existentes:
 *   Bloque E · «Conducta compartida»               → el enunciado (`prompt`)
 *   Bloque E · «Señal que favorece cada hipótesis» → las dos opciones opuestas
 *   Bloque E · «Interés de fondo diferente»        → `discriminatesBy`
 *
 * En inglés. El original español va al lado de cada opción.
 */

const V = "draft-nine-0.1";

/** Los pares que existen sólo por adyacencia en el círculo, no por análisis. */
const POR_ALA =
  "[LÍMITE] Par presente en el canon sólo como vecindad de ala: el Bloque E de " +
  "los tipos 2 a 8 lista exactamente sus dos alas. Separa peor que 1↔6 o 9↔6, " +
  "que sí salen de un análisis de confundibilidad. A medir en el piloto.";

export const DIFFERENTIATORS_NINE: DifferentiationQuestion[] = [
  {
    id: "D_1_3",
    candidatePair: ["1", "3"],
    // «Ambos revisan y pulen el resultado antes de entregarlo»
    prompt: "Think about the last thing you polished before sending it out. If you had known for certain nobody would look closely, what would have changed?",
    discriminatesBy: "corrección interna (1) frente a reconocimiento y eficacia visible (3)",
    maxInfluence: 2,
    sourceRefs: ["Plantilla_maestra_nueve_eneatipos.docx, Eneatipo 1, Bloque E, frente a tipo 3"],
    version: V,
    options: [
      {
        id: "D_1_3_a",
        // «Corrección que persiste sin audiencia favorece 1»
        text: "Nothing. It should be right either way.",
        signals: ["estándar independiente de audiencia"],
        evidenceTags: ["1:estandar-independiente-de-audiencia"],
        counterevidenceTags: [],
        // Mantener el estándar aunque nadie mire: exigencia interior independiente
        // del efecto de imagen.
        patternWeights: { "1": 2 },
        confidence: "high",
        sourceStatus: "fuente"
      },
      {
        id: "D_1_3_b",
        // «Exigencia que decae sin audiencia favorece 3»
        text: "I'd have done a lot less. Most of that effort was for how it would land.",
        signals: ["exigencia que decae sin audiencia"],
        evidenceTags: ["3:exigencia-decae-sin-audiencia"],
        counterevidenceTags: [],
        // Reducir el esfuerzo cuando desaparece la audiencia: el acabado estaba al
        // servicio de cómo sería recibido.
        patternWeights: { "3": 2 },
        confidence: "high",
        sourceStatus: "fuente"
      }
    ]
  },

  {
    id: "D_1_8",
    candidatePair: ["1", "8"],
    // «Tríada del instinto: enfado latente, dificultad para mostrar vulnerabilidad»
    prompt: "When something genuinely angers you, where does the anger tend to go?",
    discriminatesBy: "corrección e integridad (1) frente a autonomía y posición propia (8)",
    maxInfluence: 2,
    sourceRefs: [
      "Plantilla_maestra_nueve_eneatipos.docx, Eneatipo 1, Bloque E, frente a tipo 8",
      "[FUENTE EXTERNA] La conducta compartida se apoya en la tríada del instinto"
    ],
    version: V,
    options: [
      {
        id: "D_1_8_a",
        // «Enfado contenido y autocrítico favorece 1»
        text: "Inward. I hold it in, and a good part of it ends up aimed at myself.",
        signals: ["enfado contenido y autocrítico"],
        evidenceTags: ["1:enfado-contenido"],
        counterevidenceTags: [],
        // Contener la ira y volverla contra uno mismo es compatible con el 1, pero
        // es señal CONDUCTUAL y sensible al subtipo. Por eso +1 y no +2.
        patternWeights: { "1": 1 },
        confidence: "high",
        sourceStatus: "fuente"
      },
      {
        id: "D_1_8_b",
        // «Enfado expresado hacia fuera favorece 8»
        text: "Outward, and quickly. People know where they stand with me.",
        signals: ["enfado expresado hacia fuera"],
        evidenceTags: ["8:enfado-hacia-fuera"],
        counterevidenceTags: [],
        // Expresar ira inmediata aparece también en 1 sexual y 6 contrafóbico. Debe
        // pesar menos que una diferencia motivacional.
        patternWeights: { "8": 1 },
        confidence: "high",
        sourceStatus: "fuente"
      }
    ]
  },

  {
    id: "D_1_2",
    candidatePair: ["1", "2"],
    // «Ambos se orientan a hacer "lo correcto" por los demás»
    prompt: "You did something for someone that cost you real effort. What decided that you'd do it?",
    discriminatesBy: "corrección e integridad (1) frente a amor y conexión (2)",
    maxInfluence: 2,
    sourceRefs: [
      "Plantilla_maestra_nueve_eneatipos.docx, Eneatipo 2, Bloque E, frente a tipo 1",
      "[LÍMITE] Falso positivo declarado: en cuidado profesional ambos muestran conductas de servicio casi idénticas"
    ],
    version: V,
    options: [
      {
        id: "D_1_2_a",
        // «Corregir en función de un estándar (aunque no genere cercanía) favorece 1»
        text: "It was the right thing to do. It didn't make us any closer, and that wasn't the point.",
        signals: ["estándar por encima del vínculo"],
        evidenceTags: ["1:estandar-independiente-de-audiencia"],
        counterevidenceTags: [],
        // Ayudar por principio, sin buscar más cercanía: obligación impersonal
        // frente a vinculación.
        patternWeights: { "1": 2 },
        confidence: "high",
        sourceStatus: "fuente"
      },
      {
        id: "D_1_2_b",
        // «Ayudar en función de la relación favorece 2»
        text: "It was for them. I'd have found it hard to leave it alone, given who they are to me.",
        signals: ["ayuda en función del vínculo"],
        evidenceTags: ["2:ayuda-por-vinculo"],
        counterevidenceTags: [],
        // El vínculo concreto pone la ayuda al servicio de la conexión.
        patternWeights: { "2": 2 },
        confidence: "high",
        sourceStatus: "fuente"
      }
    ]
  },

  {
    id: "D_2_3",
    candidatePair: ["2", "3"],
    // «Ambos gestionan activamente cómo los perciben los demás»
    prompt: "When you think about how you'd want to be remembered at work, which is closer?",
    discriminatesBy: "amor y conexión personal (2) frente a reconocimiento y eficacia (3)",
    maxInfluence: 2,
    sourceRefs: [
      "Plantilla_maestra_nueve_eneatipos.docx, Eneatipo 2, Bloque E, frente a tipo 3",
      "[LÍMITE] Falso positivo declarado: en liderazgo de equipo ambos se muestran igual de solícitos"
    ],
    version: V,
    options: [
      {
        id: "D_2_3_a",
        // «Foco en la relación y el vínculo favorece 2»
        text: "As someone people could come to. What I'd mind losing is the closeness.",
        signals: ["foco en el vínculo"],
        evidenceTags: ["2:foco-en-vinculo"],
        counterevidenceTags: [],
        // Temer perder la cercanía: valor obtenido mediante relación.
        patternWeights: { "2": 2 },
        confidence: "high",
        sourceStatus: "fuente"
      },
      {
        id: "D_2_3_b",
        // «Foco en el logro y el resultado favorece 3»
        text: "As someone who delivered. What I'd mind losing is the track record.",
        signals: ["foco en el logro"],
        evidenceTags: ["3:foco-en-logro"],
        counterevidenceTags: [],
        // Temer perder el historial de logros: valor obtenido mediante rendimiento.
        patternWeights: { "3": 2 },
        confidence: "high",
        sourceStatus: "fuente"
      }
    ]
  },

  {
    id: "D_3_4",
    candidatePair: ["3", "4"],
    // «Ambos son muy sensibles a cómo se los percibe y cuidan activamente su imagen»
    prompt: "You care how you come across. What would bother you more?",
    discriminatesBy: "eficacia y reconocimiento (3) frente a autenticidad y particularidad (4)",
    maxInfluence: 2,
    sourceRefs: [
      "Plantilla_maestra_nueve_eneatipos.docx, Eneatipo 3, Bloque E, frente a tipo 4",
      "[LÍMITE] Falso positivo declarado: en entornos creativos de alto perfil ambos necesitan destacar",
      POR_ALA
    ],
    version: V,
    options: [
      {
        id: "D_3_4_a",
        // «Proyectar éxito convencional favorece 3»
        text: "Being seen as someone who didn't make it.",
        signals: ["éxito convencional"],
        evidenceTags: ["3:foco-en-logro"],
        counterevidenceTags: [],
        // Lo insoportable es aparecer como alguien que no triunfó: fracaso de
        // competencia e imagen.
        patternWeights: { "3": 2 },
        confidence: "medium",
        sourceStatus: "fuente"
      },
      {
        id: "D_3_4_b",
        // «Cultivar una identidad deliberadamente distinta favorece 4»
        text: "Being seen as ordinary — as basically interchangeable with everyone else.",
        signals: ["identidad deliberadamente distinta"],
        evidenceTags: ["4:teme-lo-corriente"],
        counterevidenceTags: [],
        // Lo insoportable es ser indistinguible: amenaza central del 4.
        patternWeights: { "4": 2 },
        confidence: "medium",
        sourceStatus: "fuente"
      }
    ]
  },

  {
    id: "D_4_5",
    candidatePair: ["4", "5"],
    // «Ambos tienden a replegarse hacia un mundo interior propio»
    prompt: "You pull back into your own world fairly often. What is it like in there?",
    discriminatesBy: "repliegue emocional e intenso (4) frente a repliegue racional y desapegado (5)",
    maxInfluence: 2,
    sourceRefs: [
      "Plantilla_maestra_nueve_eneatipos.docx, Eneatipo 4, Bloque E, frente a tipo 5",
      POR_ALA
    ],
    version: V,
    options: [
      {
        id: "D_4_5_a",
        // «Repliegue cargado de emoción e intensidad favorece 4»
        text: "Full of feeling. That's rather the point of going there.",
        signals: ["repliegue con carga emocional"],
        evidenceTags: ["4:intensidad"],
        counterevidenceTags: [],
        // El retiro sirve para entrar más adentro de la experiencia emocional y
        // sostenerla.
        patternWeights: { "4": 2 },
        confidence: "medium",
        sourceStatus: "fuente"
      },
      {
        id: "D_4_5_b",
        // «Repliegue hacia lo racional y desapegado favorece 5»
        text: "Quiet and analytical. It's a relief precisely because the feeling is turned down.",
        signals: ["repliegue racional y desapegado"],
        evidenceTags: ["5:repliegue-racional"],
        counterevidenceTags: [],
        // El retiro reduce la demanda afectiva y permite comprender desde la
        // distancia.
        patternWeights: { "5": 2 },
        confidence: "medium",
        sourceStatus: "fuente"
      }
    ]
  },

  {
    id: "D_5_6",
    candidatePair: ["5", "6"],
    // «Ambos son cautelosos y prefieren tener información antes de actuar»
    prompt: "Something matters and you don't feel ready. Where does the preparation actually happen?",
    discriminatesBy: "acumular conocimiento en soledad (5) frente a buscar apoyo externo (6)",
    maxInfluence: 2,
    sourceRefs: [
      "Plantilla_maestra_nueve_eneatipos.docx, Eneatipo 5, Bloque E, frente a tipo 6",
      POR_ALA
    ],
    version: V,
    options: [
      {
        id: "D_5_6_a",
        // «Acumular conocimiento en soledad favorece 5»
        text: "On my own. I read and work it out until I've got enough to go on.",
        signals: ["conocimiento acumulado en soledad"],
        evidenceTags: ["5:prepara-en-soledad"],
        counterevidenceTags: [],
        // Prepararse en solitario hasta confiar en la propia comprensión: autonomía
        // cognitiva.
        patternWeights: { "5": 2 },
        confidence: "medium",
        sourceStatus: "fuente"
      },
      {
        id: "D_5_6_b",
        // «Buscar apoyo o consulta externa favorece 6»
        text: "With other people. I need to talk it through before I trust my read on it.",
        signals: ["consulta externa"],
        evidenceTags: ["6:consulta-repetida"],
        counterevidenceTags: [],
        // Hablarlo antes de fiarse de la propia lectura: búsqueda de contraste y
        // respaldo.
        patternWeights: { "6": 2 },
        confidence: "medium",
        sourceStatus: "fuente"
      }
    ]
  },

  {
    id: "D_6_7",
    candidatePair: ["6", "7"],
    // «Ambos tienen la mente ocupada de forma constante y les cuesta el silencio interno»
    prompt: "Your mind rarely goes quiet. When it's running, what is it running on?",
    discriminatesBy: "qué puede salir mal (6) frente a qué otra cosa mejor podría estar pasando (7)",
    maxInfluence: 2,
    sourceRefs: [
      "Plantilla_maestra_nueve_eneatipos.docx, Eneatipo 6, Bloque E, frente a tipo 7",
      POR_ALA
    ],
    version: V,
    options: [
      {
        id: "D_6_7_a",
        // «Gira en torno a qué puede salir mal, favorece 6»
        text: "What could go wrong, and what I'd do about it.",
        signals: ["rumia de riesgo"],
        evidenceTags: ["6:anticipa-peligro"],
        counterevidenceTags: [],
        // La actividad mental gira sobre amenazas, consecuencias y planes de
        // contingencia.
        patternWeights: { "6": 2 },
        confidence: "medium",
        sourceStatus: "fuente"
      },
      {
        id: "D_6_7_b",
        // «Gira en torno a qué otra cosa mejor podría estar pasando, favorece 7»
        text: "What else I could be doing — usually something better than this.",
        signals: ["búsqueda de mejor opción"],
        evidenceTags: ["7:busca-mejor-opcion"],
        counterevidenceTags: [],
        // La actividad mental gira sobre alternativas mejores y escape de la
        // limitación presente.
        patternWeights: { "7": 2 },
        confidence: "medium",
        sourceStatus: "fuente"
      }
    ]
  },

  {
    id: "D_7_8",
    candidatePair: ["7", "8"],
    // «Ambos resisten que otros les digan qué hacer y valoran su libertad de acción»
    prompt: "Someone tries to pin you down to something you don't want. What do you actually do?",
    discriminatesBy: "resistencia por evitación (7) frente a resistencia por confrontación (8)",
    maxInfluence: 2,
    sourceRefs: [
      "Plantilla_maestra_nueve_eneatipos.docx, Eneatipo 7, Bloque E, frente a tipo 8",
      POR_ALA
    ],
    version: V,
    options: [
      {
        id: "D_7_8_a",
        // «Evitando el compromiso (huyendo), favorece 7»
        text: "I keep it vague and leave myself a way out. I don't argue about it.",
        signals: ["evita el compromiso"],
        evidenceTags: ["7:evita-compromiso"],
        counterevidenceTags: [],
        // Mantener opciones abiertas es compatible con el 7, pero la vaguedad y la
        // evitación del conflicto también describen al 9. Por eso +1.
        patternWeights: { "7": 1 },
        confidence: "medium",
        sourceStatus: "fuente"
      },
      {
        id: "D_7_8_b",
        // «Confrontando directamente, favorece 8»
        text: "I push back, there and then, and we settle it.",
        signals: ["confrontación directa"],
        evidenceTags: ["8:confronta-directo"],
        counterevidenceTags: [],
        // Resistirse en el momento y forzar la resolución: oposición directa al
        // control.
        patternWeights: { "8": 2 },
        confidence: "medium",
        sourceStatus: "fuente"
      }
    ]
  },

  {
    id: "D_8_9",
    candidatePair: ["8", "9"],
    // «Tríada del instinto: autonomía, dificultad con la vulnerabilidad»
    prompt: "When you're angry and it isn't the moment to say so, what happens to it?",
    discriminatesBy: "enfado expresado (8) frente a enfado contenido y pasivo-agresivo (9)",
    maxInfluence: 2,
    sourceRefs: [
      "Plantilla_maestra_nueve_eneatipos.docx, Eneatipo 8, Bloque E, frente a tipo 9",
      "[FUENTE EXTERNA] La conducta compartida se apoya en la tríada del instinto",
      POR_ALA
    ],
    version: V,
    options: [
      {
        id: "D_8_9_a",
        // «Enfado expresado hacia fuera de forma directa favorece 8»
        text: "It comes out anyway. Waiting for a better moment isn't really something I manage.",
        signals: ["enfado expresado hacia fuera"],
        evidenceTags: ["8:enfado-hacia-fuera"],
        counterevidenceTags: [],
        // La ira visible favorece al 8 frente al 9, pero sigue siendo manifestación
        // conductual influida por contexto y subtipo.
        patternWeights: { "8": 1 },
        confidence: "medium",
        sourceStatus: "fuente"
      },
      {
        id: "D_8_9_b",
        // «Enfado contenido y pasivo-agresivo favorece 9»
        text: "It goes underground. I say nothing, and it comes out sideways later.",
        signals: ["enfado contenido, pasivo-agresivo"],
        evidenceTags: ["9:enfado-contenido"],
        counterevidenceTags: [],
        // Enterrar la ira y expresarla de lado favorece al 9, pero no es exclusivo:
        // aparece también en 1, 2 y 6.
        patternWeights: { "9": 1 },
        confidence: "medium",
        sourceStatus: "fuente"
      }
    ]
  },

  {
    id: "D_2_9",
    candidatePair: ["2", "9"],
    // «Ambos priorizan las necesidades o deseos de otros por encima de los propios»
    prompt: "You put someone else's wishes ahead of your own. Which is nearer to what happened?",
    discriminatesBy: "ofrecerse activamente buscando reconocimiento (2) frente a ceder sin ofrecer nada (9)",
    maxInfluence: 2,
    sourceRefs: ["Plantilla_maestra_nueve_eneatipos.docx, Eneatipo 9, Bloque E, frente a tipo 2"],
    version: V,
    options: [
      {
        id: "D_2_9_a",
        // «Ofrecerse activamente buscando reconocimiento favorece 2»
        text: "I offered — I stepped in and did more than was asked.",
        signals: ["ofrecimiento activo"],
        evidenceTags: ["2:ayuda-no-solicitada"],
        counterevidenceTags: [],
        // El 2 se adelanta, detecta la necesidad y ofrece más de lo pedido:
        // importancia relacional.
        patternWeights: { "2": 2 },
        confidence: "high",
        sourceStatus: "fuente"
      },
      {
        id: "D_2_9_b",
        // «Ceder sin ofrecer ayuda activa favorece 9»
        text: "I just went along with it. I didn't offer anything, I only stopped pushing my own.",
        signals: ["cesión sin ofrecimiento"],
        evidenceTags: ["9:cede-sin-decidir"],
        counterevidenceTags: [],
        // El 9 no ofrece: deja de sostener su propio deseo y se acomoda.
        patternWeights: { "9": 2 },
        confidence: "high",
        sourceStatus: "fuente"
      }
    ]
  },

  {
    id: "D_5_9",
    candidatePair: ["5", "9"],
    // «Ambos pueden retirarse o desconectarse ante el conflicto»
    prompt: "There's a conflict and you step back from it. What does stepping back look like?",
    discriminatesBy: "ceder a la posición ajena (9) frente a aislarse sin comprometerse con ninguna (5)",
    maxInfluence: 2,
    sourceRefs: ["Plantilla_maestra_nueve_eneatipos.docx, Eneatipo 9, Bloque E, frente a tipo 5"],
    version: V,
    options: [
      // ORDEN CORREGIDO. Estaba al revés: la `_a` describía al 9 y la `_b` al 5,
      // contra la convención que siguen los otros trece (`D_X_Y_a` apunta a X).
      // Los pesos se pusieron por SIGNIFICADO, así que salieron correctos — pero
      // el nombre decía lo contrario que el contenido, y eso es exactamente el
      // tipo de trampa que hace que el siguiente cambio la meta. Se intercambian
      // los textos en vez de renombrar los ids, para no invalidar referencias.
      // Hay una guarda en `nine-patterns.test.ts` que ya no deja repetirlo.
      {
        id: "D_5_9_a",
        // «Aislarse sin comprometerse con ninguna posición favorece 5»
        text: "I withdraw altogether. I don't take their side either — I just stop being in it.",
        signals: ["aislamiento sin tomar posición"],
        evidenceTags: ["5:se-aisla"],
        counterevidenceTags: [],
        // Retirarse sin fusionarse con ninguna posición: separación y desapego.
        patternWeights: { "5": 2 },
        confidence: "high",
        sourceStatus: "fuente"
      },
      {
        id: "D_5_9_b",
        // «Ceder activamente a la posición ajena favorece 9»
        text: "I end up going along with their side of it.",
        signals: ["cesión a la posición ajena"],
        evidenceTags: ["9:cede-sin-decidir"],
        counterevidenceTags: [],
        // Acabar adoptando la posición ajena: acomodación y pérdida de contacto con
        // la agenda propia.
        patternWeights: { "9": 2 },
        confidence: "high",
        sourceStatus: "fuente"
      }
    ]
  }
];
